/**
 * Onboarding (PLAN §10.1, R6/R7/R19):
 * tryb → piesek → kolor → imię → PIN → register / login / profile-select / recovery.
 * UI wyłącznie po polsku; kliknięcia = realne API (nie makieta).
 */
import Phaser from 'phaser';
import {
  DOG_BREED_IDS,
  DOG_COLORS,
  DOGS,
  type DogBreedId,
  type ProfileSummary,
} from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { SaveLocal } from '../../core/SaveLocal.js';
import { audioBus } from '../../core/AudioBus.js';
import {
  fetchMe,
  isValidParentPin,
  loginAccount,
  loadProfileDraft,
  persistAuth,
  recoverAccountApi,
  registerAccount,
  resolveLoginProfileId,
  saveProfileDraft,
  selectProfileApi,
} from './authClient.js';
import { nameSuggestions, resolveDogName } from './nameHelpers.js';

type Step =
  | 'mode'
  | 'breed'
  | 'color'
  | 'name'
  | 'pin'
  | 'login'
  | 'profileSelect'
  | 'recovery'
  | 'showRecovery'
  | 'busy';

const BREED_COLORS: Record<DogBreedId, number> = {
  maltese: 0xfff5ee,
  corgi: 0xf4a460,
  poodle: 0xe6e0f8,
  husky: 0xb0c4de,
  dachshund: 0xcd853f,
  golden: 0xffd700,
};

export class OnboardingScene extends BaseScene {
  static readonly KEY = 'Onboarding';

  private step: Step = 'mode';
  private breed: DogBreedId = 'maltese';
  private color: string = DOG_COLORS[0]!;
  private dogNameInput = '';
  private pin = '';
  private recoveryCodeInput = '';
  private pendingRecoveryCode = '';
  private pendingProfiles: ProfileSummary[] = [];
  private pendingAccountId = '';
  private uiRoot: Phaser.GameObjects.Container | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private htmlWrap: HTMLDivElement | null = null;
  private nameInputEl: HTMLInputElement | null = null;
  private pinInputEl: HTMLInputElement | null = null;
  private recoveryInputEl: HTMLInputElement | null = null;
  private destroyed = false;

  constructor() {
    super(OnboardingScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_wardrobe');
  }

  /** Miękkie ilustrowane tło pod kreatorem (re-dodawane po każdym clearUi). */
  private drawSoftBg(): void {
    this.cameras.main.setBackgroundColor(0xe6e0f8);
    const bg = this.addRealBg('bg_wardrobe', -20);
    if (bg) bg.setAlpha(0.32);
    this.add
      .rectangle(this.gameW / 2, this.gameH / 2, this.gameW, this.gameH, 0xe6e0f8, 0.42)
      .setDepth(-19);
  }

  create(): void {
    this.destroyed = false;
    this.cameras.main.setBackgroundColor(0xe6e0f8);
    audioBus.unlock();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.teardownDom();
      this.destroyed = true;
    });
    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.teardownDom();
      this.destroyed = true;
    });
    void this.bootstrap();
  }

  private async bootstrap(): Promise<void> {
    if (SaveLocal.getToken()) {
      const me = await fetchMe();
      if (this.destroyed) return;
      if (me.ok) {
        if (me.data.onboardingCompleted) {
          getSceneRouter().go('Town');
          return;
        }
        getSceneRouter().go('Fortune');
        return;
      }
    }
    const draft = loadProfileDraft();
    if (draft?.breed && DOG_BREED_IDS.includes(draft.breed as DogBreedId)) {
      this.breed = draft.breed as DogBreedId;
      this.color = draft.color || DOGS[this.breed].defaultColor;
      this.dogNameInput = draft.dogName || '';
    }
    this.registry.set('dogBreed', this.breed);
    this.step = 'mode';
    this.render();
  }

  private clearUi(): void {
    this.teardownDom();
    if (this.uiRoot) {
      this.uiRoot.destroy(true);
      this.uiRoot = null;
    }
    this.statusText = null;
    this.children.removeAll(true);
  }

  private teardownDom(): void {
    if (this.htmlWrap?.parentElement) {
      this.htmlWrap.parentElement.removeChild(this.htmlWrap);
    }
    this.htmlWrap = null;
    this.nameInputEl = null;
    this.pinInputEl = null;
    this.recoveryInputEl = null;
  }

  private setStatus(msg: string): void {
    if (this.statusText) {
      this.statusText.setText(msg);
    }
  }

  private render(): void {
    this.clearUi();
    this.drawSoftBg();
    const cx = this.gameW / 2;
    const kids: Phaser.GameObjects.GameObject[] = [];

    const title = this.add
      .text(cx, 48, this.titleForStep(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    kids.push(title);

    const sub = this.add
      .text(cx, 96, this.subtitleForStep(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#6b5f7a',
        align: 'center',
        wordWrap: { width: this.gameW - 48 },
      })
      .setOrigin(0.5);
    kids.push(sub);

    this.statusText = this.add
      .text(cx, this.gameH - 36, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
        align: 'center',
        wordWrap: { width: this.gameW - 40 },
      })
      .setOrigin(0.5);
    kids.push(this.statusText);

    switch (this.step) {
      case 'mode':
        kids.push(...this.buildMode(cx));
        break;
      case 'breed':
        kids.push(...this.buildBreedGrid(cx));
        break;
      case 'color':
        kids.push(...this.buildColorGrid(cx));
        break;
      case 'name':
        kids.push(...this.buildNameStep(cx));
        break;
      case 'pin':
        kids.push(...this.buildPinStep(cx, 'register'));
        break;
      case 'login':
        kids.push(...this.buildPinStep(cx, 'login'));
        break;
      case 'recovery':
        kids.push(...this.buildRecoveryStep(cx));
        break;
      case 'profileSelect':
        kids.push(...this.buildProfileSelect(cx));
        break;
      case 'showRecovery':
        kids.push(...this.buildShowRecovery(cx));
        break;
      case 'busy':
        kids.push(
          this.add
            .text(cx, this.gameH / 2, 'Chwileczkę…', {
              fontFamily: 'system-ui, sans-serif',
              fontSize: '28px',
              color: '#4a3f55',
              fontStyle: 'bold',
            })
            .setOrigin(0.5),
        );
        break;
    }

    this.uiRoot = this.add.container(0, 0, kids);
  }

  private titleForStep(): string {
    switch (this.step) {
      case 'mode':
        return 'Witaj w Pieskowie!';
      case 'breed':
        return 'Wybierz pieska';
      case 'color':
        return 'Wybierz kolorek';
      case 'name':
        return 'Jak ma na imię?';
      case 'pin':
        return 'PIN opiekuna';
      case 'login':
        return 'Witaj z powrotem!';
      case 'recovery':
        return 'Odzyskaj konto';
      case 'profileSelect':
        return 'Który piesek?';
      case 'showRecovery':
        return 'Kod odzyskania';
      case 'busy':
        return 'Prawie gotowe…';
    }
  }

  private subtitleForStep(): string {
    switch (this.step) {
      case 'mode':
        return 'Nowe przygody Misi i Loli czekają';
      case 'breed':
        return 'Dotknij ulubionego przyjaciela';
      case 'color':
        return 'Jaki kolor Ci się podoba?';
      case 'name':
        return 'Wpisz imię albo wybierz podpowiedź';
      case 'pin':
        return '4–8 cyfr — zapamiętaj je z mamą/tatą';
      case 'login': {
        const acc = SaveLocal.getAccountId();
        return acc
          ? 'Wpisz PIN opiekuna (konto z tego urządzenia)'
          : 'Brak zapisanego konta — użyj odzyskania albo zrób nowe';
      }
      case 'recovery':
        return 'Kod odzyskania (od rodzica) + nowy PIN 4–8 cyfr';
      case 'profileSelect':
        return 'Wybierz profil, którym chcesz grać';
      case 'showRecovery':
        return 'Zapisz ten kod z mamą/tatą — pokazujemy go tylko raz!';
      case 'busy':
        return '';
    }
  }

  private buildMode(cx: number): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];

    const draft = loadProfileDraft();
    const savedName = draft?.dogName?.trim();
    const hasSavedPup = !!(savedName && draft?.breed);
    const baseY = hasSavedPup ? this.gameH / 2 + 10 : this.gameH / 2 - 40;

    // „Wróć do …" tylko dla jawnego trybu offline — nie omija PIN konta online.
    if (hasSavedPup && draft?.offline === true) {
      const resume = UiKit.button(this, {
        x: cx,
        y: this.gameH / 2 - 130,
        width: 380,
        height: 96,
        label: `▶ Wróć do ${savedName} (offline)`,
        fill: 0xffd1e8,
        onClick: () => {
          this.resumeLocal();
        },
      });
      out.push(resume);
    }

    out.push(
      UiKit.button(this, {
        x: cx,
        y: baseY,
        width: 320,
        height: 80,
        label: '✨ Nowe konto',
        fill: 0xb8e6d0,
        onClick: () => {
          this.step = 'breed';
          this.render();
        },
      }),
      UiKit.button(this, {
        x: cx,
        y: baseY + 100,
        width: 320,
        height: 80,
        label: '🔑 Mam już konto',
        fill: 0xbde0fe,
        onClick: () => {
          this.pin = '';
          this.step = 'login';
          this.render();
        },
      }),
      UiKit.button(this, {
        x: cx,
        y: baseY + 200,
        width: 280,
        height: 64,
        label: '🛟 Odzyskaj konto',
        fill: 0xffe4b5,
        onClick: () => {
          this.pin = '';
          this.recoveryCodeInput = '';
          this.step = 'recovery';
          this.render();
        },
      }),
    );
    return out;
  }

  /**
   * Wejście powracającego gracza offline — tylko draft z flagą offline.
   * Nie tworzy fałszywego Bearer tokenu (Sol WAVE6 P1).
   */
  private resumeLocal(): void {
    const draft = loadProfileDraft();
    if (!draft?.offline || !draft.dogName?.trim() || !draft.breed) {
      this.setStatus('Zaloguj się PIN-em albo zrób nowe konto');
      return;
    }
    // Flaga trybu, nie udajemy tokenu API.
    SaveLocal.setJson('pieskowo.authMode', 'offline');
    if (!SaveLocal.getAccountId()) SaveLocal.setAccountId('local-acc');
    if (!SaveLocal.getProfileId()) SaveLocal.setProfileId('local-prof');
    // Usuń ewentualny martwy token online — RunClient ma iść offline.
    const tok = SaveLocal.getToken();
    if (tok && !tok.startsWith('offline-')) {
      /* keep real token if any */
    } else {
      SaveLocal.setToken(''); // clear if empty helper doesn't exist - use remove
      try {
        localStorage.removeItem('pieskowo.token');
      } catch {
        /* */
      }
    }
    getSceneRouter().go('Town');
  }

  private buildBreedGrid(cx: number): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const cols = 3;
    const tileW = 200;
    const tileH = Math.max(MIN_TOUCH + 40, 120);
    const gapX = 24;
    const gapY = 20;
    const startX = cx - ((cols - 1) * (tileW + gapX)) / 2;
    const startY = 160;

    DOG_BREED_IDS.forEach((id, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (tileW + gapX);
      const y = startY + row * (tileH + gapY);
      const def = DOGS[id];
      const selected = this.breed === id;
      const fill = BREED_COLORS[id];
      const bg = this.add
        .rectangle(x, y, tileW, tileH, fill)
        .setStrokeStyle(selected ? 5 : 3, selected ? 0xff69b4 : 0xffffff)
        .setInteractive({ useHandCursor: true });
      const iconKey = `breed_${id}`;
      const emoji = this.textures.exists(iconKey)
        ? this.add.image(x, y - 16, iconKey).setDisplaySize(78, 78).setOrigin(0.5)
        : this.add.text(x, y - 22, '🐕', { fontSize: '36px' }).setOrigin(0.5);
      const name = this.add
        .text(x, y + 18, def.namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#4a3f55',
          fontStyle: 'bold',
          align: 'center',
          wordWrap: { width: tileW - 12 },
        })
        .setOrigin(0.5);
      const personality = this.add
        .text(x, y + 44, def.personalityPl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#6b5f7a',
          align: 'center',
          wordWrap: { width: tileW - 16 },
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => {
        this.breed = id;
        this.color = DOGS[id].defaultColor;
        this.registry.set('dogBreed', id);
        this.step = 'color';
        this.render();
      });
      out.push(bg, emoji, name, personality);
    });

    out.push(
      UiKit.button(this, {
        x: 100,
        y: this.gameH - 80,
        width: 140,
        label: '← Wstecz',
        fill: 0xffdab9,
        onClick: () => {
          this.step = 'mode';
          this.render();
        },
      }),
    );
    return out;
  }

  private buildColorGrid(cx: number): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const size = Math.max(MIN_TOUCH + 8, 72);
    const gap = 16;
    const cols = 4;
    const startX = cx - ((cols - 1) * (size + gap)) / 2;
    const startY = 200;

    DOG_COLORS.forEach((hex, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (size + gap);
      const y = startY + row * (size + gap);
      const fill = Phaser.Display.Color.HexStringToColor(hex).color;
      const selected = this.color.toLowerCase() === hex.toLowerCase();
      const circle = this.add
        .circle(x, y, size / 2, fill)
        .setStrokeStyle(selected ? 6 : 3, selected ? 0xff69b4 : 0xffffff)
        .setInteractive({ useHandCursor: true });
      circle.on('pointerdown', () => {
        this.color = hex;
        this.render();
      });
      out.push(circle);
    });

    const previewFill = Phaser.Display.Color.HexStringToColor(this.color).color;
    out.push(
      this.add.circle(cx, 492, 62, previewFill, 0.4).setStrokeStyle(4, 0xffffff),
      this.textures.exists(`breed_${this.breed}`)
        ? this.add.image(cx, 474, `breed_${this.breed}`).setDisplaySize(124, 124).setOrigin(0.5)
        : this.add.text(cx, 480, '🐕', { fontSize: '40px' }).setOrigin(0.5),
      this.add
        .text(cx, 540, DOGS[this.breed].namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    out.push(
      UiKit.button(this, {
        x: 100,
        y: this.gameH - 80,
        width: 140,
        label: '← Wstecz',
        fill: 0xffdab9,
        onClick: () => {
          this.step = 'breed';
          this.render();
        },
      }),
      UiKit.button(this, {
        x: this.gameW - 120,
        y: this.gameH - 80,
        width: 180,
        label: 'Dalej →',
        fill: 0xb8e6d0,
        onClick: () => {
          this.step = 'name';
          this.render();
        },
      }),
    );
    return out;
  }

  private buildNameStep(cx: number): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const suggestions = nameSuggestions(this.breed, Date.now() % 10000);
    const ySug = 200;
    suggestions.forEach((name, i) => {
      const x = cx - 220 + i * 220;
      out.push(
        UiKit.button(this, {
          x,
          y: ySug,
          width: 200,
          height: MIN_TOUCH + 8,
          label: name,
          fill: 0xffb6c1,
          onClick: () => {
            this.dogNameInput = name;
            if (this.nameInputEl) this.nameInputEl.value = name;
            this.setStatus(`Wybrane: ${name}`);
          },
        }),
      );
    });

    this.mountNameInput();

    out.push(
      UiKit.button(this, {
        x: 100,
        y: this.gameH - 80,
        width: 140,
        label: '← Wstecz',
        fill: 0xffdab9,
        onClick: () => {
          this.step = 'color';
          this.render();
        },
      }),
      UiKit.button(this, {
        x: this.gameW - 120,
        y: this.gameH - 80,
        width: 180,
        label: 'Dalej →',
        fill: 0xb8e6d0,
        onClick: () => {
          const raw = this.nameInputEl?.value ?? this.dogNameInput;
          const resolved = resolveDogName(raw);
          this.dogNameInput = resolved.dogName;
          if (!resolved.moderationOk) {
            this.setStatus(`Spróbujmy: ${resolved.dogName}`);
            if (this.nameInputEl) this.nameInputEl.value = resolved.dogName;
          } else if (resolved.usedFallback) {
            this.setStatus(`Losowe imię: ${resolved.dogName}`);
          }
          this.step = 'pin';
          this.render();
        },
      }),
    );
    return out;
  }

  private buildPinStep(
    cx: number,
    mode: 'register' | 'login',
  ): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    this.mountPinInput(mode === 'login');

    if (mode === 'login') {
      const acc = SaveLocal.getAccountId();
      if (!acc) {
        out.push(
          this.add
            .text(cx, 170, 'Na tym urządzeniu nie ma zapisanego konta.\nUżyj „Odzyskaj konto” albo załóż nowe.', {
              fontFamily: 'system-ui, sans-serif',
              fontSize: '18px',
              color: '#8b4513',
              align: 'center',
              wordWrap: { width: this.gameW - 60 },
            })
            .setOrigin(0.5),
        );
      }
    }

    const pads = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', 'OK'];
    const cols = 3;
    const size = Math.max(MIN_TOUCH, 64);
    const startX = cx - ((cols - 1) * (size + 12)) / 2;
    const startY = mode === 'login' && !SaveLocal.getAccountId() ? 300 : 280;
    pads.forEach((label, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (size + 12);
      const y = startY + row * (size + 12);
      const fill = label === 'OK' ? 0xb8e6d0 : label === '⌫' ? 0xffdab9 : 0xfff8f0;
      const bg = this.add
        .rectangle(x, y, size, size, fill)
        .setStrokeStyle(3, 0xffffff)
        .setInteractive({ useHandCursor: true });
      const txt = this.add
        .text(x, y, label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: label === 'OK' || label === '⌫' ? '22px' : '28px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => this.onPad(label, mode));
      out.push(bg, txt);
    });

    out.push(
      UiKit.button(this, {
        x: 100,
        y: this.gameH - 80,
        width: 140,
        label: '← Wstecz',
        fill: 0xffdab9,
        onClick: () => {
          this.pin = '';
          this.step = mode === 'login' ? 'mode' : 'name';
          this.render();
        },
      }),
    );

    if (mode === 'login') {
      out.push(
        UiKit.button(this, {
          x: this.gameW - 150,
          y: this.gameH - 80,
          width: 220,
          height: 56,
          label: '🛟 Odzyskaj',
          fill: 0xffe4b5,
          onClick: () => {
            this.pin = '';
            this.recoveryCodeInput = '';
            this.step = 'recovery';
            this.render();
          },
        }),
      );
    }
    return out;
  }

  private buildRecoveryStep(cx: number): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    this.mountRecoveryInputs();

    const pads = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', 'OK'];
    const cols = 3;
    const size = Math.max(MIN_TOUCH, 56);
    const startX = cx - ((cols - 1) * (size + 10)) / 2;
    const startY = 320;
    pads.forEach((label, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (size + 10);
      const y = startY + row * (size + 10);
      const fill = label === 'OK' ? 0xb8e6d0 : label === '⌫' ? 0xffdab9 : 0xfff8f0;
      const bg = this.add
        .rectangle(x, y, size, size, fill)
        .setStrokeStyle(3, 0xffffff)
        .setInteractive({ useHandCursor: true });
      const txt = this.add
        .text(x, y, label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: label === 'OK' || label === '⌫' ? '20px' : '24px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => this.onPad(label, 'recovery'));
      out.push(bg, txt);
    });

    out.push(
      UiKit.button(this, {
        x: 100,
        y: this.gameH - 80,
        width: 140,
        label: '← Wstecz',
        fill: 0xffdab9,
        onClick: () => {
          this.pin = '';
          this.recoveryCodeInput = '';
          this.step = 'mode';
          this.render();
        },
      }),
    );
    return out;
  }

  private buildProfileSelect(cx: number): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const profiles = this.pendingProfiles;
    const tileH = Math.max(MIN_TOUCH + 16, 88);
    const gap = 16;
    const startY = 180;
    const maxShow = Math.min(profiles.length, 5);

    for (let i = 0; i < maxShow; i++) {
      const p = profiles[i]!;
      const y = startY + i * (tileH + gap);
      const label = p.displayName?.trim() || p.dogName || 'Piesek';
      const breedLabel = DOGS[p.breed as DogBreedId]?.namePl ?? p.breed;
      out.push(
        UiKit.button(this, {
          x: cx,
          y,
          width: Math.min(420, this.gameW - 48),
          height: tileH,
          label: `🐕 ${label} · ${breedLabel}`,
          fill: 0xffd1e8,
          onClick: () => {
            void this.finishProfileSelect(p.profileId);
          },
        }),
      );
    }

    if (profiles.length === 0) {
      out.push(
        this.add
          .text(cx, this.gameH / 2, 'Brak profili na koncie', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '22px',
            color: '#6b5f7a',
          })
          .setOrigin(0.5),
      );
    }

    out.push(
      UiKit.button(this, {
        x: 100,
        y: this.gameH - 80,
        width: 140,
        label: '← Wstecz',
        fill: 0xffdab9,
        onClick: () => {
          this.pendingProfiles = [];
          this.step = 'mode';
          this.render();
        },
      }),
    );
    return out;
  }

  private buildShowRecovery(cx: number): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const code = this.pendingRecoveryCode || '—';

    out.push(
      this.add
        .rectangle(cx, this.gameH / 2 - 20, Math.min(520, this.gameW - 40), 160, 0xffffff, 0.92)
        .setStrokeStyle(4, 0xbde0fe),
      this.add
        .text(cx, this.gameH / 2 - 50, 'Kod dla mamy/taty:', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: '#6b5f7a',
        })
        .setOrigin(0.5),
      this.add
        .text(cx, this.gameH / 2 + 10, code, {
          fontFamily: 'ui-monospace, monospace',
          fontSize: code.length > 16 ? '22px' : '28px',
          color: '#2d1f3d',
          fontStyle: 'bold',
          align: 'center',
          wordWrap: { width: Math.min(480, this.gameW - 80) },
        })
        .setOrigin(0.5),
      UiKit.button(this, {
        x: cx,
        y: this.gameH / 2 + 140,
        width: 320,
        height: 80,
        label: '✅ Zapisałam / Zapisałem',
        fill: 0xb8e6d0,
        onClick: () => {
          this.pendingRecoveryCode = '';
          getSceneRouter().go('Fortune');
        },
      }),
    );
    return out;
  }

  private onPad(label: string, mode: 'register' | 'login' | 'recovery'): void {
    if (label === '⌫') {
      this.pin = this.pin.slice(0, -1);
    } else if (label === 'OK') {
      if (mode === 'recovery') {
        void this.submitRecovery();
      } else {
        void this.submitPin(mode === 'login');
      }
      return;
    } else if (this.pin.length < 8) {
      this.pin += label;
    }
    if (this.pinInputEl) this.pinInputEl.value = '•'.repeat(this.pin.length);
    this.setStatus(this.pin.length ? `PIN: ${'•'.repeat(this.pin.length)}` : '');
  }

  private async submitPin(isLogin: boolean): Promise<void> {
    if (!isValidParentPin(this.pin)) {
      this.setStatus('PIN: wpisz 4–8 cyfr');
      return;
    }

    this.step = 'busy';
    this.render();

    if (isLogin) {
      await this.handleLogin();
      return;
    }
    await this.handleRegister();
  }

  private async handleLogin(): Promise<void> {
    const accountId = SaveLocal.getAccountId() ?? undefined;
    const credential = this.pin;
    if (!accountId) {
      this.pin = '';
      this.step = 'login';
      this.render();
      this.setStatus('Brak konta na urządzeniu — użyj „Odzyskaj konto”');
      return;
    }

    const res = await loginAccount({
      accountId,
      parentCredential: credential,
    });
    this.pin = '';
    if (this.pinInputEl) this.pinInputEl.value = '';
    if (this.destroyed) return;

    if (res.ok) {
      SaveLocal.setToken(res.data.token);
      SaveLocal.setAccountId(res.data.accountId);
      this.pendingAccountId = res.data.accountId;
      this.pendingProfiles = res.data.profiles;

      const profileId = resolveLoginProfileId(res.data);
      if (profileId) {
        await this.finishProfileSelect(profileId);
        return;
      }
      // Wiele profili — UI wyboru
      this.step = 'profileSelect';
      this.render();
      return;
    }

    this.step = 'login';
    this.render();
    this.setStatus(
      res.offline
        ? 'Brak sieci — spróbuj później albo zrób nowe konto (offline)'
        : res.code === 'UNAUTHORIZED'
          ? 'Nie udało się zalogować — sprawdź PIN'
          : 'Coś poszło nie tak — spróbuj jeszcze raz',
    );
  }

  private async handleRegister(): Promise<void> {
    const resolved = resolveDogName(this.dogNameInput);
    const dogName = resolved.dogName;
    saveProfileDraft({
      dogName,
      breed: this.breed,
      color: this.color,
      characterKind: 'dog',
    });

    const credential = this.pin;
    const res = await registerAccount({
      parentCredential: credential,
      dogName,
      breed: this.breed,
      color: this.color,
      characterKind: 'dog',
    });
    this.pin = '';
    if (this.pinInputEl) this.pinInputEl.value = '';
    if (this.destroyed) return;

    if (res.ok) {
      persistAuth({
        token: res.data.token,
        accountId: res.data.accountId,
        profileId: res.data.profileId,
      });
      saveProfileDraft({
        dogName,
        breed: this.breed,
        color: this.color,
        characterKind: 'dog',
      });
      this.pendingRecoveryCode = res.data.recoveryCode;
      this.step = 'showRecovery';
      this.render();
      return;
    }

    if (res.offline || res.status === 501 || res.code === 'NOT_IMPLEMENTED' || res.status === 0) {
      saveProfileDraft({
        dogName,
        breed: this.breed,
        color: this.color,
        characterKind: 'dog',
        offline: true,
      });
      // Offline: bez fałszywego Bearer tokenu — RunClient idzie w stub.
      try {
        localStorage.removeItem('pieskowo.token');
      } catch {
        /* */
      }
      SaveLocal.setAccountId('local-acc');
      SaveLocal.setProfileId('local-prof');
      SaveLocal.setJson('pieskowo.authMode', 'offline');
      // Fortune wymaga API — offline idziemy prosto do Town (lokalny start).
      getSceneRouter().go('Town');
      return;
    }

    this.step = 'pin';
    this.render();
    this.setStatus(
      res.code === 'MODERATION_REJECTED'
        ? `Spróbujmy imienia: ${dogName}`
        : 'Coś poszło nie tak — spróbuj jeszcze raz',
    );
  }

  private async finishProfileSelect(profileId: string): Promise<void> {
    this.step = 'busy';
    this.render();

    // Token z loginu/recovery już w SaveLocal — profile-select rotuje sesję.
    const sel = await selectProfileApi(profileId);
    if (this.destroyed) return;

    if (sel.ok) {
      persistAuth({
        token: sel.data.token,
        accountId: this.pendingAccountId || SaveLocal.getAccountId() || '',
        profileId: sel.data.profileId,
      });
      // Sync draft z wybranym profilem (imię do „Wróć do…”)
      const p = sel.data.profile;
      if (p.dogName && p.breed) {
        saveProfileDraft({
          dogName: p.dogName,
          breed: p.breed,
          color: p.color,
          characterKind: p.characterKind === 'dragon' ? 'dragon' : 'dog',
        });
      }
      this.pendingProfiles = [];
      const me = await fetchMe();
      if (this.destroyed) return;
      if (me.ok && me.data.onboardingCompleted) {
        getSceneRouter().go('Town');
        return;
      }
      getSceneRouter().go('Fortune');
      return;
    }

    // Gdy serwer już auto-wiązał profil (1 profil), /me może działać bez select.
    if (SaveLocal.getToken()) {
      const me = await fetchMe();
      if (this.destroyed) return;
      if (me.ok) {
        SaveLocal.setProfileId(profileId);
        if (me.data.onboardingCompleted) {
          getSceneRouter().go('Town');
          return;
        }
        getSceneRouter().go('Fortune');
        return;
      }
    }

    this.step = 'profileSelect';
    this.render();
    this.setStatus(
      sel.offline ? 'Brak sieci — spróbuj za chwilę' : 'Nie udało się wybrać profilu',
    );
  }

  private async submitRecovery(): Promise<void> {
    const code = (this.recoveryInputEl?.value ?? this.recoveryCodeInput).trim();
    this.recoveryCodeInput = code;
    if (code.length < 8) {
      this.setStatus('Wpisz pełny kod odzyskania');
      return;
    }
    if (!isValidParentPin(this.pin)) {
      this.setStatus('Nowy PIN: wpisz 4–8 cyfr');
      return;
    }

    this.step = 'busy';
    this.render();

    const newPin = this.pin;
    const res = await recoverAccountApi({
      recoveryCode: code,
      newParentCredential: newPin,
    });
    this.pin = '';
    if (this.pinInputEl) this.pinInputEl.value = '';
    if (this.destroyed) return;

    if (res.ok) {
      SaveLocal.setToken(res.data.token);
      SaveLocal.setAccountId(res.data.accountId);
      this.pendingAccountId = res.data.accountId;
      this.pendingProfiles = res.data.profiles;
      this.recoveryCodeInput = '';

      if (res.data.profiles.length === 1) {
        await this.finishProfileSelect(res.data.profiles[0]!.profileId);
        return;
      }
      if (res.data.profiles.length > 1) {
        this.step = 'profileSelect';
        this.render();
        return;
      }
      this.step = 'mode';
      this.render();
      this.setStatus('Konto odzyskane, ale bez profili — załóż nowe konto');
      return;
    }

    this.step = 'recovery';
    this.render();
    this.setStatus(
      res.offline
        ? 'Brak sieci — spróbuj później'
        : res.code === 'UNAUTHORIZED'
          ? 'Niepoprawny kod odzyskania'
          : 'Nie udało się odzyskać konta',
    );
  }

  private mountNameInput(): void {
    try {
      const parent =
        (this.game.canvas.parentElement as HTMLElement | null) ??
        document.getElementById('game-container') ??
        document.body;
      const wrap = document.createElement('div');
      wrap.setAttribute('data-onboarding-name', '1');
      wrap.style.cssText =
        'position:absolute;left:50%;top:48%;transform:translate(-50%,-50%);' +
        'z-index:9999;pointer-events:auto;';
      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 12;
      input.placeholder = 'Imię pieska…';
      input.value = this.dogNameInput;
      input.setAttribute('aria-label', 'Imię pieska');
      input.autocomplete = 'off';
      input.style.cssText =
        'width:min(360px,70vw);height:56px;font-size:24px;border-radius:16px;' +
        'border:4px solid #ffb6c1;padding:0 16px;font-family:system-ui,sans-serif;' +
        'color:#4a3f55;background:#fff;text-align:center;';
      input.addEventListener('input', () => {
        this.dogNameInput = input.value;
      });
      wrap.appendChild(input);
      parent.style.position = parent.style.position || 'relative';
      parent.appendChild(wrap);
      this.htmlWrap = wrap;
      this.nameInputEl = input;
    } catch {
      this.htmlWrap = null;
      this.nameInputEl = null;
    }
  }

  private mountPinInput(isLogin: boolean): void {
    try {
      const parent =
        (this.game.canvas.parentElement as HTMLElement | null) ??
        document.getElementById('game-container') ??
        document.body;
      const wrap = document.createElement('div');
      wrap.setAttribute('data-onboarding-pin', '1');
      wrap.style.cssText =
        'position:absolute;left:50%;top:28%;transform:translate(-50%,-50%);' +
        'z-index:9999;pointer-events:auto;display:flex;flex-direction:column;align-items:center;gap:8px;';
      const input = document.createElement('input');
      input.type = 'password';
      input.inputMode = 'numeric';
      input.pattern = '[0-9]*';
      input.maxLength = 8;
      input.placeholder = isLogin ? 'PIN…' : 'Ustaw PIN…';
      input.setAttribute('aria-label', 'PIN opiekuna');
      input.autocomplete = 'off';
      input.style.cssText =
        'width:min(280px,60vw);height:56px;font-size:28px;border-radius:16px;' +
        'border:4px solid #bde0fe;padding:0 16px;font-family:system-ui,sans-serif;' +
        'color:#4a3f55;background:#fff;text-align:center;letter-spacing:8px;';
      input.addEventListener('input', () => {
        const v = input.value.replace(/\D/g, '').slice(0, 8);
        input.value = v;
        this.pin = v;
      });
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          void this.submitPin(isLogin);
        }
      });
      wrap.appendChild(input);
      parent.style.position = parent.style.position || 'relative';
      parent.appendChild(wrap);
      this.htmlWrap = wrap;
      this.pinInputEl = input;
      input.focus();
    } catch {
      this.htmlWrap = null;
      this.pinInputEl = null;
    }
  }

  private mountRecoveryInputs(): void {
    try {
      const parent =
        (this.game.canvas.parentElement as HTMLElement | null) ??
        document.getElementById('game-container') ??
        document.body;
      const wrap = document.createElement('div');
      wrap.setAttribute('data-onboarding-recovery', '1');
      wrap.style.cssText =
        'position:absolute;left:50%;top:22%;transform:translate(-50%,-50%);' +
        'z-index:9999;pointer-events:auto;display:flex;flex-direction:column;align-items:center;gap:10px;';

      const code = document.createElement('input');
      code.type = 'text';
      code.autocomplete = 'off';
      code.spellcheck = false;
      code.placeholder = 'Kod odzyskania…';
      code.setAttribute('aria-label', 'Kod odzyskania konta');
      code.value = this.recoveryCodeInput;
      code.style.cssText =
        'width:min(360px,78vw);height:52px;font-size:18px;border-radius:14px;' +
        'border:4px solid #ffe4b5;padding:0 14px;font-family:ui-monospace,monospace;' +
        'color:#4a3f55;background:#fff;text-align:center;';
      code.addEventListener('input', () => {
        this.recoveryCodeInput = code.value.trim();
      });

      const pin = document.createElement('input');
      pin.type = 'password';
      pin.inputMode = 'numeric';
      pin.pattern = '[0-9]*';
      pin.maxLength = 8;
      pin.placeholder = 'Nowy PIN…';
      pin.setAttribute('aria-label', 'Nowy PIN opiekuna');
      pin.autocomplete = 'off';
      pin.style.cssText =
        'width:min(280px,60vw);height:52px;font-size:24px;border-radius:14px;' +
        'border:4px solid #bde0fe;padding:0 14px;font-family:system-ui,sans-serif;' +
        'color:#4a3f55;background:#fff;text-align:center;letter-spacing:6px;';
      pin.addEventListener('input', () => {
        const v = pin.value.replace(/\D/g, '').slice(0, 8);
        pin.value = v;
        this.pin = v;
      });
      pin.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          void this.submitRecovery();
        }
      });

      wrap.appendChild(code);
      wrap.appendChild(pin);
      parent.style.position = parent.style.position || 'relative';
      parent.appendChild(wrap);
      this.htmlWrap = wrap;
      this.recoveryInputEl = code;
      this.pinInputEl = pin;
      code.focus();
    } catch {
      this.htmlWrap = null;
      this.recoveryInputEl = null;
      this.pinInputEl = null;
    }
  }
}

function cryptoRandom(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return String(Date.now());
}

export default OnboardingScene;
